import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Chip,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Divider,
  Tabs,
  Tab,
  Stack,
  Badge
} from '@mui/material';
import { useSnackbar } from '../components/common/SnackbarProvider';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import { useAppSelector } from '../hooks/useAppSelector';
import { useAppDispatch } from '../hooks/useAppDispatch';
import { setUser } from '../store/slices/authSlice';
import axiosInstance from '../api/axiosConfig';
import { useNavigate } from 'react-router-dom';
import { useFeatureFlags } from '../features';
import SwimMyFamilyCard from '../components/swim/SwimMyFamilyCard';
import SwimTodayCard from '../components/swim/SwimTodayCard';

interface HouseholdMember {
  id: string;
  member_id: string | null;
  non_member_dependent_id?: number | null;
  first_name: string;
  last_name: string;
  member_type: string;
  relationship_to_primary: string | null;
  is_checked_in: boolean;
  check_in_data: any;
}

const DashboardPage: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const flags = useFeatureFlags();
  const isStaffish = user?.role === 'staff' || user?.role === 'admin' || user?.role === 'superuser';
  const isNonMember = (user?.membership_status || '').toLowerCase().startsWith('non_member');
  const [checkInStatus, setCheckInStatus] = useState<any>(null);
  const [householdMembers, setHouseholdMembers] = useState<HouseholdMember[]>([]);
  const [selectedHouseholdMembers, setSelectedHouseholdMembers] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [householdLoading, setHouseholdLoading] = useState(false);
  const [checkInTab, setCheckInTab] = useState(0);

  useEffect(() => {
    fetchUserProfile();
  }, []);

  useEffect(() => {
    // Only fetch check-in status for members, not staff/admin
    if (user?.role === 'member') {
      fetchCheckInStatus();
    }
  }, [user?.role]);

  useEffect(() => {
    // Fetch household status when user data is loaded
    if (user) {
      fetchHouseholdCheckInStatus();
    }
  }, [user]);

  const fetchUserProfile = async () => {
    try {
      const response = await axiosInstance.get('/members/me');
      if (response.data.data) {
        dispatch(setUser(response.data.data));
      }
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
    }
  };

  const fetchCheckInStatus = async () => {
    try {
      const response = await axiosInstance.get('/check-ins/me/status');
      setCheckInStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch check-in status:', error);
    }
  };

  const fetchHouseholdCheckInStatus = async () => {
    // Allow PRIMARY/SECONDARY members (uppercase from backend) or non-member account holders
    if (user?.member_type !== 'PRIMARY' && user?.member_type !== 'SECONDARY' && !isNonMember) {
      return;
    }

    try {
      const response = await axiosInstance.get('/check-ins/me/household-status');
      if (response.data.success) {
        setHouseholdMembers(response.data.household_members);
      }
    } catch (error) {
      console.error('Failed to fetch household check-in status:', error);
    }
  };

  const handleHouseholdMemberToggle = (memberId: string) => {
    setSelectedHouseholdMembers(prev => {
      if (prev.includes(memberId)) {
        return prev.filter(id => id !== memberId);
      } else {
        return [...prev, memberId];
      }
    });
  };

  const handleHouseholdCheckIn = async () => {
    if (selectedHouseholdMembers.length === 0) {
      snackbar.error('Please select at least one household member to check in');
      return;
    }

    setHouseholdLoading(true);
    try {
      const response = await axiosInstance.post('/check-ins/me/household-check-in', {
        household_member_ids: selectedHouseholdMembers,
        location: 'Main Club'
      });
      snackbar.success(response.data.message);
      setSelectedHouseholdMembers([]);
      await fetchHouseholdCheckInStatus();
    } catch (err: any) {
      snackbar.error(err.response?.data?.error || 'Failed to check in household members');
    } finally {
      setHouseholdLoading(false);
    }
  };

  const handleHouseholdCheckOut = async () => {
    if (selectedHouseholdMembers.length === 0) {
      snackbar.error('Please select at least one household member to check out');
      return;
    }

    setHouseholdLoading(true);
    try {
      const response = await axiosInstance.post('/check-ins/me/household-check-out', {
        household_member_ids: selectedHouseholdMembers
      });
      snackbar.success(response.data.message);
      setSelectedHouseholdMembers([]);
      await fetchHouseholdCheckInStatus();
    } catch (err: any) {
      snackbar.error(err.response?.data?.error || 'Failed to check out household members');
    } finally {
      setHouseholdLoading(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedHouseholdMembers.length === householdMembers.length) {
      setSelectedHouseholdMembers([]);
    } else {
      setSelectedHouseholdMembers(householdMembers.map(m => m.id));
    }
  };

  const handleQuickCheckInAll = async () => {
    const allMemberIds = householdMembers.map(m => m.id);
    setHouseholdLoading(true);
    try {
      const response = await axiosInstance.post('/check-ins/me/household-check-in', {
        household_member_ids: allMemberIds,
        location: 'Main Club'
      });
      snackbar.success(response.data.message);
      setSelectedHouseholdMembers([]);
      await fetchHouseholdCheckInStatus();
    } catch (err: any) {
      snackbar.error(err.response?.data?.error || 'Failed to check in household members');
    } finally {
      setHouseholdLoading(false);
    }
  };

  const handleCheckIn = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.post('/check-ins/me/check-in', {
        location: 'Main Club'
      });
      snackbar.success(response.data.message);
      await fetchCheckInStatus();
    } catch (err: any) {
      snackbar.error(err.response?.data?.error || 'Failed to check in');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckOut = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.post('/check-ins/me/check-out', {});
      snackbar.success(response.data.message);
      await fetchCheckInStatus();
    } catch (err: any) {
      snackbar.error(err.response?.data?.error || 'Failed to check out');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Welcome back, {user?.first_name} {user?.last_name}!
        </Typography>

        <Box sx={{ display: 'flex', gap: 3, mt: 2, flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: 300 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {user?.role === 'staff' || user?.role === 'admin' || user?.role === 'superuser'
                    ? 'Employee Information'
                    : 'Membership Information'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Status: {user?.membership_status}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {user?.role === 'staff' || user?.role === 'admin' || user?.role === 'superuser'
                    ? 'Hired Date: '
                    : 'Join Date: '}
                  {user?.joined_date ? (() => {
                    const [year, month, day] = user.joined_date.split('-');
                    return `${month}/${day}/${year}`;
                  })() : 'N/A'}
                </Typography>
              </CardContent>
            </Card>
          </Box>

          {/* Only show Club Check-In for members, not staff/admin */}
          {user?.role === 'member' && (
            <Box sx={{ flex: 1, minWidth: 300 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    Club Check-In
                    {checkInStatus?.is_checked_in && (
                      <Chip
                        label="Checked In"
                        color="success"
                        size="small"
                        icon={<CheckCircleIcon />}
                      />
                    )}
                  </Typography>

                {/* Show tabs only if household members exist */}
                {(user?.member_type === 'PRIMARY' || user?.member_type === 'SECONDARY' || isNonMember) && householdMembers.length > 0 ? (
                  <>
                    <Tabs
                      value={checkInTab}
                      onChange={(e, newValue) => setCheckInTab(newValue)}
                      sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
                    >
                      <Tab
                        icon={<PersonIcon />}
                        iconPosition="start"
                        label="Just Me"
                      />
                      <Tab
                        icon={<PeopleIcon />}
                        iconPosition="start"
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            Household
                            <Chip
                              label={householdMembers.length}
                              size="small"
                              color="primary"
                              sx={{ height: 20, fontSize: '0.75rem' }}
                            />
                          </Box>
                        }
                      />
                    </Tabs>

                    {checkInTab === 0 ? (
                      // Personal Check-In Tab
                      checkInStatus?.is_checked_in ? (
                        <Box>
                          <Typography variant="body2" color="text.secondary" paragraph>
                            You checked in at {checkInStatus.data?.check_in_time ?
                              new Date(checkInStatus.data.check_in_time).toLocaleTimeString('en-US', {
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true
                              }) : 'N/A'}
                          </Typography>
                          {checkInStatus.data?.location && (
                            <Typography variant="body2" color="text.secondary" paragraph>
                              Location: {checkInStatus.data.location}
                            </Typography>
                          )}
                          <Button
                            variant="contained"
                            color="error"
                            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <LogoutIcon />}
                            onClick={handleCheckOut}
                            disabled={loading}
                            fullWidth
                          >
                            {loading ? 'Checking Out...' : 'Check Out'}
                          </Button>
                        </Box>
                      ) : (
                        <Box>
                          <Typography variant="body2" color="text.secondary" paragraph>
                            Check in to let us know you're at the club!
                          </Typography>
                          <Button
                            variant="contained"
                            color="primary"
                            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
                            onClick={handleCheckIn}
                            disabled={loading}
                            fullWidth
                          >
                            {loading ? 'Checking In...' : 'Check In'}
                          </Button>
                        </Box>
                      )
                    ) : (
                      // Household Check-In Tab
                      <Box>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            Select household members to check in or out
                          </Typography>
                          <Button
                            size="small"
                            onClick={handleSelectAll}
                            disabled={householdLoading}
                          >
                            {selectedHouseholdMembers.length === householdMembers.length ? 'Deselect All' : 'Select All'}
                          </Button>
                        </Stack>

                        <FormGroup>
                          {householdMembers.map((member) => (
                            <FormControlLabel
                              key={member.id}
                              control={
                                <Checkbox
                                  checked={selectedHouseholdMembers.includes(member.id)}
                                  onChange={() => handleHouseholdMemberToggle(member.id)}
                                  disabled={householdLoading}
                                />
                              }
                              label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography>
                                    {member.first_name} {member.last_name}
                                  </Typography>
                                  {member.relationship_to_primary && (
                                    <Typography variant="caption" color="text.secondary">
                                      ({member.relationship_to_primary})
                                    </Typography>
                                  )}
                                  {member.is_checked_in && (
                                    <Chip
                                      label="Checked In"
                                      color="success"
                                      size="small"
                                      icon={<CheckCircleIcon />}
                                    />
                                  )}
                                </Box>
                              }
                              sx={{ mb: 1 }}
                            />
                          ))}
                        </FormGroup>

                        <Divider sx={{ my: 2 }} />

                        <Stack direction="column" spacing={1}>
                          <Button
                            variant="outlined"
                            color="primary"
                            startIcon={householdLoading ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
                            onClick={handleQuickCheckInAll}
                            disabled={householdLoading}
                            fullWidth
                          >
                            {householdLoading ? 'Checking In...' : 'Check In Everyone'}
                          </Button>
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                            <Button
                              variant="contained"
                              color="primary"
                              startIcon={householdLoading ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
                              onClick={handleHouseholdCheckIn}
                              disabled={householdLoading || selectedHouseholdMembers.length === 0}
                              sx={{ flex: 1 }}
                            >
                              {householdLoading ? 'Checking In...' : 'Check In Selected'}
                            </Button>
                            <Button
                              variant="contained"
                              color="error"
                              startIcon={householdLoading ? <CircularProgress size={20} color="inherit" /> : <LogoutIcon />}
                              onClick={handleHouseholdCheckOut}
                              disabled={householdLoading || selectedHouseholdMembers.length === 0}
                              sx={{ flex: 1 }}
                            >
                              {householdLoading ? 'Checking Out...' : 'Check Out Selected'}
                            </Button>
                          </Stack>
                        </Stack>
                      </Box>
                    )}
                  </>
                ) : (
                  // No household members - show only personal check-in
                  <>
                    {checkInStatus?.is_checked_in ? (
                      <>
                        <Typography variant="body2" color="text.secondary" paragraph>
                          You checked in at {checkInStatus.data?.check_in_time ?
                            new Date(checkInStatus.data.check_in_time).toLocaleTimeString('en-US', {
                              hour: 'numeric',
                              minute: '2-digit',
                              hour12: true
                            }) : 'N/A'}
                        </Typography>
                        {checkInStatus.data?.location && (
                          <Typography variant="body2" color="text.secondary" paragraph>
                            Location: {checkInStatus.data.location}
                          </Typography>
                        )}
                        <Button
                          variant="contained"
                          color="error"
                          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <LogoutIcon />}
                          onClick={handleCheckOut}
                          disabled={loading}
                          fullWidth
                        >
                          {loading ? 'Checking Out...' : 'Check Out'}
                        </Button>
                      </>
                    ) : (
                      <>
                        <Typography variant="body2" color="text.secondary" paragraph>
                          Check in to let us know you're at the club!
                        </Typography>
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
                          onClick={handleCheckIn}
                          disabled={loading}
                          fullWidth
                        >
                          {loading ? 'Checking In...' : 'Check In'}
                        </Button>
                      </>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </Box>
          )}

          {/* Kids Club Quick Access Card */}
          {flags.kidsClub && (
            <Box sx={{ flex: 1, minWidth: 300 }}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ChildCareIcon color="primary" />
                    Kids Club
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {user?.role === 'member'
                      ? 'Book and manage your children\'s Kids Club reservations'
                      : 'Manage Kids Club check-ins, check-outs, and emergency alerts'}
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={() =>
                      navigate(user?.role === 'member' ? '/kids-club' : '/kids-club/staff')
                    }
                    sx={{ mt: 1 }}
                  >
                    {user?.role === 'member' ? 'Book Kids Club' : 'Kids Club Staff Dashboard'}
                  </Button>
                </CardContent>
              </Card>
            </Box>
          )}

          {flags.swim && user?.role === 'member' && (
            <Box sx={{ flex: 1, minWidth: 300 }}>
              <SwimMyFamilyCard />
            </Box>
          )}

          {flags.swim && isStaffish && (
            <Box sx={{ flex: 1, minWidth: 300 }}>
              <SwimTodayCard />
            </Box>
          )}
        </Box>
      </Box>
    </Container>
  );
};

export default DashboardPage;
